// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button } from '@imprivata-cloud/components';
import type { MenuProps } from 'antd';
import { ConfigProvider, Dropdown, Modal } from 'antd';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IdvConnectionSummary } from '../../../api/types';
import EmptyStateIllustration from '../../../assets/empty-list.svg?react';
import { TRANSLATION_ROOT } from './IdvConnections.types';

// --- IdvEmptyState ---

interface IdvEmptyStateProps {
  isLoading: boolean;
  addMenuItems: MenuProps['items'];
}

export const IdvEmptyState = ({ isLoading, addMenuItems }: IdvEmptyStateProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="idv-empty-state">
      <EmptyStateIllustration className="empty-state-illustration" />
      <div className="empty-state-text-container">
        <p className="empty-state-text">{t(`${TRANSLATION_ROOT}.empty-state-text`)}</p>
        {/*
          Disable antd's click "wave": on mouse-down antd injects a transient
          `.ant-wave` node beside the button that animates an expanding outline
          outward — a ghost ring that belongs to no button. The header add-button
          disables it the same way; the empty-state button needs its own wrapper.
        */}
        <ConfigProvider wave={{ disabled: true }}>
          <Dropdown
            menu={{ items: addMenuItems }}
            disabled={isLoading}
            overlayClassName="idv-integration-dropdown idv-empty-state-dropdown"
            trigger={['click']}
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            <Button
              disabled={isLoading}
              className="add-integration-button"
              data-button-type="Secondary"
              data-button-state="Enabled"
              data-button-size="Medium"
            >
              <span className="button-label">{t(`${TRANSLATION_ROOT}.empty-state-link`)}</span>
              <ChevronDown size={16} className={menuOpen ? 'icon-right open' : 'icon-right'} />
            </Button>
          </Dropdown>
        </ConfigProvider>
      </div>
    </div>
  );
};

// --- IdvDeleteModal ---

interface IdvDeleteModalProps {
  connection: IdvConnectionSummary | null;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const IdvDeleteModal = ({
  connection,
  isLoading,
  onConfirm,
  onCancel,
}: IdvDeleteModalProps) => {
  const { t } = useTranslation();
  return (
    // Disable antd's click "wave" (the transient `.ant-wave` node that animates an expanding
    // outline — a "ghost" ring that belongs to no button) on the Remove/Cancel buttons, matching
    // the treatment on the other IDV buttons.
    <ConfigProvider wave={{ disabled: true }}>
      <Modal
        title={null}
        open={!!connection}
        width={428}
        closable={false}
        footer={null}
        maskClosable={false}
        className="idv-delete-modal"
      >
        <div className="idv-delete-modal-body">
          <HelpCircle className="idv-delete-alert-icon" size={80} strokeWidth={2} />
          <h2 className="idv-delete-modal-title">
            {t(`${TRANSLATION_ROOT}.delete.confirm-title`, {
              displayName: connection?.displayName,
            })}
          </h2>
          <p className="idv-delete-modal-content">
            {t(`${TRANSLATION_ROOT}.delete.confirm-message`, {
              displayName: connection?.displayName,
            })}
          </p>
          <div className="idv-delete-modal-footer">
            <Button
              type="default"
              label={t(`${TRANSLATION_ROOT}.delete.confirm-button`)}
              onClick={onConfirm}
              disabled={isLoading}
              size="large"
              className="idv-delete-remove-button"
            />
            <Button
              type="primary"
              label={t(`${TRANSLATION_ROOT}.delete.cancel-button`)}
              onClick={onCancel}
              size="large"
              className="idv-delete-dont-remove-button"
            />
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
};
